import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  IconButton,
  LinearProgress,
  Typography,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
} from '@mui/joy';
import { X as Close, Rocket, DotsThreeVertical as MoreVert } from '@phosphor-icons/react';
import {
  useSetupProgress,
  useDismissSetupReminder,
} from '../../hooks/useSetupProgress';

export const DashboardSetupReminder: React.FC = () => {
  const navigate = useNavigate();
  const { data: progress, isLoading } = useSetupProgress();
  const dismissReminder = useDismissSetupReminder();
  const [sessionDismissed, setSessionDismissed] = useState(false);

  // Don't show if loading, no progress, already completed, or dismissed
  if (
    isLoading ||
    !progress ||
    progress.wizard_completed ||
    progress.wizard_dismissed ||
    sessionDismissed
  ) {
    return null;
  }

  const handleContinueSetup = () => {
    navigate(`/setup?step=${progress.current_step}`);
  };

  const handleDismissSession = () => {
    setSessionDismissed(true);
  };

  const handleDismissPermanently = async () => {
    await dismissReminder.mutateAsync();
  };

  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        mb: 3,
        background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
        border: '1px solid',
        borderColor: 'primary.200',
        [theme.getColorSchemeSelector('dark')]: {
          background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.12) 0%, rgba(156, 39, 176, 0.12) 100%)',
          borderColor: 'primary.700',
        },
      })}
    >
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={(theme) => ({
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'primary.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                [theme.getColorSchemeSelector('dark')]: {
                  backgroundColor: 'primary.800',
                },
              })}
            >
              <Rocket color="var(--joy-palette-primary-600)" />
            </Box>
            <Box>
              <Typography
                level="title-md"
                sx={(theme) => ({
                  [theme.getColorSchemeSelector('dark')]: {
                    color: 'common.white',
                  },
                })}
              >
                Complete Your ApexOS Setup
              </Typography>
              <Typography
                level="body-sm"
                sx={(theme) => ({
                  color: 'neutral.600',
                  [theme.getColorSchemeSelector('dark')]: {
                    color: 'neutral.300',
                  },
                })}
              >
                You're {progress.completed_steps_count} of 7 steps through
                setup
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Dropdown>
              <MenuButton
                slots={{ root: IconButton }}
                slotProps={{
                  root: { variant: 'plain', color: 'neutral', size: 'sm' },
                }}
              >
                <MoreVert />
              </MenuButton>
              <Menu placement="bottom-end">
                <MenuItem onClick={handleDismissPermanently}>
                  Don't show again
                </MenuItem>
              </Menu>
            </Dropdown>
            <IconButton
              variant="plain"
              color="neutral"
              size="sm"
              onClick={handleDismissSession}
              aria-label="Dismiss for now"
            >
              <Close />
            </IconButton>
          </Box>
        </Box>

        {/* Progress bar */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 0.5,
            }}
          >
            <Typography
              level="body-xs"
              sx={(theme) => ({
                color: 'neutral.600',
                [theme.getColorSchemeSelector('dark')]: {
                  color: 'neutral.300',
                },
              })}
            >
              Progress
            </Typography>
            <Typography
              level="body-xs"
              sx={(theme) => ({
                color: 'neutral.600',
                [theme.getColorSchemeSelector('dark')]: {
                  color: 'neutral.300',
                },
              })}
            >
              {progress.progress_percentage}%
            </Typography>
          </Box>
          <LinearProgress
            determinate
            value={progress.progress_percentage}
            sx={{
              '--LinearProgress-thickness': '8px',
              '--LinearProgress-radius': '4px',
            }}
          />
        </Box>

        {/* Action button */}
        <Button
          variant="solid"
          color="primary"
          onClick={handleContinueSetup}
          sx={{ width: '100%' }}
        >
          Continue Setup
        </Button>
      </Box>
    </Card>
  );
};

export default DashboardSetupReminder;
